import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../context/AuthContext";
import { Plus, Pencil, Trash2, Check, X, Save, RefreshCw, Mail, Globe } from "lucide-react";
import { NETWORK_ROLES } from "../../constants/roles";

const ROLES = NETWORK_ROLES;
const STATUSES = ["all", "published", "pending", "rejected"];
const emptyPro = { name: "", role: "Editor", pronouns: "", location: "", bio: "", neurodivergence: "", skills: "", email: "", website: "" };

export default function AdminProfessionals() {
  const [pros, setPros] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setBusy(true); setError(null);
    try {
      const r = await axios.get(`${API}/admin/professionals`, { withCredentials: true });
      setPros(r.data || []);
    } catch (e) {
      setError("Could not load directory.");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => statusFilter === "all" ? pros : pros.filter(p => p.status === statusFilter), [pros, statusFilter]);
  const counts = useMemo(() => ({
    published: pros.filter(p => p.status === "published").length,
    pending: pros.filter(p => p.status === "pending").length,
    rejected: pros.filter(p => p.status === "rejected").length,
  }), [pros]);

  return (
    <div data-testid="admin-tab-professionals">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading font-black text-[#0A0A0A] text-2xl tracking-tight">Network directory</h2>
          <p className="text-sm text-[#2C2C2C]">Approve member submissions and curate the directory.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={busy} data-testid="admin-pros-refresh" className="bg-transparent border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FFF8E7] rounded-none px-4 py-2 text-sm font-medium inline-flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Refresh</button>
          <button onClick={() => setShowAdd(true)} data-testid="admin-pros-add" className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] border-2 border-[#0A0A0A] pop-shadow rounded-none px-5 py-2 text-sm font-medium inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add professional</button>
        </div>
      </div>

      {error && <div className="bg-[#FF2E63] text-[#FFF8E7] border-2 border-[#0A0A0A] p-3 mb-4" role="alert">{error}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Stat label="Total" value={pros.length} bg="bg-[#FFD93D]" />
        <Stat label="Published" value={counts.published} bg="bg-[#1A5DFF]" text="text-[#FFF8E7]" />
        <Stat label="Pending" value={counts.pending} bg="bg-[#FF2E63]" text="text-[#FFF8E7]" />
        <Stat label="Rejected" value={counts.rejected} bg="bg-white" />
      </div>

      <div className="flex flex-wrap gap-2 mb-5" role="tablist" data-testid="admin-pros-status-filters">
        {STATUSES.map(s => (
          <button key={s} role="tab" aria-selected={s === statusFilter} onClick={() => setStatusFilter(s)} data-testid={`admin-pros-filter-${s}`} className={`px-4 py-2 text-sm font-medium border-2 transition-colors ${s === statusFilter ? "bg-[#0A0A0A] text-[#FFF8E7] border-[#0A0A0A]" : "bg-transparent text-[#0A0A0A] border-[#0A0A0A] hover:bg-[#FFD93D]"}`}>{s[0].toUpperCase()+s.slice(1)}</button>
        ))}
      </div>

      <div className="space-y-3" data-testid="admin-pros-list">
        {filtered.map(p => <ProRow key={p.id} pro={p} onEdit={() => setEditing(p)} onChanged={load} />)}
        {!busy && filtered.length === 0 && <div className="text-[#2C2C2C]">No professionals in this view.</div>}
      </div>

      {showAdd && <ProFormModal title="Add professional" initial={emptyPro} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} mode="create" />}
      {editing && <ProFormModal title="Edit professional" initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} mode="edit" />}
    </div>
  );
}

function Stat({ label, value, bg, text = "text-[#0A0A0A]" }) {
  return (
    <div className={`${bg} ${text} border-2 border-[#0A0A0A] p-4`}>
      <div className="text-xs uppercase tracking-[0.18em] font-bold">{label}</div>
      <div className="font-heading font-black text-3xl mt-1">{value}</div>
    </div>
  );
}

function ProRow({ pro, onEdit, onChanged }) {
  const [busy, setBusy] = useState(false);
  const act = async (verb) => {
    if (verb === "delete" && !window.confirm(`Delete "${pro.name}"?`)) return;
    setBusy(true);
    try {
      if (verb === "delete") await axios.delete(`${API}/admin/professionals/${pro.id}`, { withCredentials: true });
      else await axios.post(`${API}/admin/professionals/${pro.id}/${verb}`, {}, { withCredentials: true });
      onChanged();
    } catch { alert("Action failed."); } finally { setBusy(false); }
  };
  const statusBg = pro.status === "published" ? "bg-[#1A5DFF] text-[#FFF8E7]" : pro.status === "pending" ? "bg-[#FFD93D] text-[#0A0A0A]" : "bg-[#0A0A0A] text-[#FFF8E7]";
  const initials = pro.name.split(" ").map(n => n[0]).slice(0, 2).join("");
  return (
    <div className="bg-white border-2 border-[#0A0A0A] p-4 md:p-5 flex flex-col md:flex-row gap-4 items-start" data-testid={`admin-pro-${pro.id}`}>
      <div className="w-16 h-16 bg-[#0A0A0A] text-[#FF2E63] flex items-center justify-center font-heading font-black text-xl flex-shrink-0">{initials}</div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className={`text-xs uppercase tracking-[0.16em] font-bold px-2 py-0.5 border-2 border-[#0A0A0A] ${statusBg}`}>{pro.status}</span>
          <span className="text-xs uppercase tracking-[0.14em] font-bold text-[#FF2E63]">{pro.role}</span>
          <span className="text-xs text-[#2C2C2C]">· {pro.location}</span>
        </div>
        <h3 className="font-heading font-bold text-lg text-[#0A0A0A] leading-tight">{pro.name}{pro.pronouns && <span className="text-xs font-normal text-[#2C2C2C] ml-2">({pro.pronouns})</span>}</h3>
        <p className="text-sm text-[#2C2C2C] mt-1 line-clamp-2">{pro.bio}</p>
        <div className="flex items-center gap-3 mt-2 text-xs text-[#2C2C2C]">
          {pro.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {pro.email}</span>}
          {pro.website && <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" /> {pro.website}</span>}
          {pro.submitter_email && pro.submitter_email !== pro.email && <span className="text-[#1A5DFF]">submitted via {pro.submitter_email}</span>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {pro.status !== "published" && <button disabled={busy} onClick={() => act("approve")} data-testid={`admin-pro-approve-${pro.id}`} className="bg-[#1A5DFF] text-[#FFF8E7] hover:bg-[#0E4ACC] border-2 border-[#0A0A0A] rounded-none px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Approve</button>}
        {pro.status !== "rejected" && <button disabled={busy} onClick={() => act("reject")} data-testid={`admin-pro-reject-${pro.id}`} className="bg-transparent text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FFF8E7] border-2 border-[#0A0A0A] rounded-none px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5"><X className="w-3.5 h-3.5" /> Reject</button>}
        <button disabled={busy} onClick={onEdit} data-testid={`admin-pro-edit-${pro.id}`} className="bg-[#FFD93D] text-[#0A0A0A] hover:bg-[#FFCB05] border-2 border-[#0A0A0A] rounded-none px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5"><Pencil className="w-3.5 h-3.5" /> Edit</button>
        <button disabled={busy} onClick={() => act("delete")} data-testid={`admin-pro-delete-${pro.id}`} className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] border-2 border-[#0A0A0A] rounded-none px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
      </div>
    </div>
  );
}

function ProFormModal({ title, initial, onClose, onSaved, mode }) {
  const [form, setForm] = useState({
    name: initial.name || "", role: initial.role || "Editor", pronouns: initial.pronouns || "",
    location: initial.location || "", bio: initial.bio || "",
    neurodivergence: Array.isArray(initial.neurodivergence) ? initial.neurodivergence.join(", ") : (initial.neurodivergence || ""),
    skills: Array.isArray(initial.skills) ? initial.skills.join(", ") : (initial.skills || ""),
    email: initial.email || "", website: initial.website || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault(); setError(null); setSubmitting(true);
    try {
      const payload = {
        ...form,
        neurodivergence: form.neurodivergence.split(",").map(t => t.trim()).filter(Boolean),
        skills: form.skills.split(",").map(t => t.trim()).filter(Boolean),
      };
      if (mode === "create") await axios.post(`${API}/admin/professionals`, payload, { withCredentials: true });
      else await axios.put(`${API}/admin/professionals/${initial.id}`, payload, { withCredentials: true });
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0A0A0A]/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="pro-form-modal">
      <form onClick={e => e.stopPropagation()} onSubmit={save} className="bg-[#FFF8E7] border-2 border-[#0A0A0A] pop-shadow max-w-3xl w-full p-8 my-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF2E63] mb-1">{mode === "create" ? "New profile" : "Edit"}</div>
            <h2 className="font-heading font-black text-[#0A0A0A] text-2xl">{title}</h2>
          </div>
          <button type="button" onClick={onClose} className="bg-[#0A0A0A] text-[#FFF8E7] hover:bg-[#FF2E63] p-2 border-2 border-[#0A0A0A]"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Full name" required><input data-testid="admin-pro-name" value={form.name} onChange={e => set("name", e.target.value)} required className={cls} /></F>
          <F label="Pronouns"><input data-testid="admin-pro-pronouns" value={form.pronouns} onChange={e => set("pronouns", e.target.value)} className={cls} /></F>
          <F label="Role" required><select data-testid="admin-pro-role" value={form.role} onChange={e => set("role", e.target.value)} className={cls} required>{ROLES.map(r => <option key={r} value={r}>{r}</option>)}</select></F>
          <F label="Location" required><input data-testid="admin-pro-location" value={form.location} onChange={e => set("location", e.target.value)} required className={cls} /></F>
          <F label="Bio" required full><textarea data-testid="admin-pro-bio" rows={3} value={form.bio} onChange={e => set("bio", e.target.value)} required className={cls} /></F>
          <F label="Neurodivergence (comma-separated)" full><input data-testid="admin-pro-nd" value={form.neurodivergence} onChange={e => set("neurodivergence", e.target.value)} className={cls} /></F>
          <F label="Skills (comma-separated)" full><input data-testid="admin-pro-skills" value={form.skills} onChange={e => set("skills", e.target.value)} className={cls} /></F>
          <F label="Public email"><input data-testid="admin-pro-email" type="email" value={form.email} onChange={e => set("email", e.target.value)} className={cls} /></F>
          <F label="Website"><input data-testid="admin-pro-website" type="url" value={form.website} onChange={e => set("website", e.target.value)} className={cls} /></F>
        </div>
        {error && <div className="mt-4 bg-[#FF2E63] text-[#FFF8E7] border-2 border-[#0A0A0A] p-3 text-sm">{error}</div>}
        <div className="mt-6 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="bg-transparent border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FFF8E7] rounded-none px-5 py-2.5 text-sm font-medium">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="admin-save-pro" className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] disabled:opacity-60 border-2 border-[#0A0A0A] pop-shadow rounded-none px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2"><Save className="w-4 h-4" /> {submitting ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

const cls = "w-full bg-white border-2 border-[#0A0A0A] text-[#0A0A0A] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF2E63] rounded-none text-sm";

function F({ label, children, required, full }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-[0.16em] font-bold text-[#0A0A0A]">{label} {required && <span className="text-[#FF2E63]">*</span>}</span>
      {children}
    </label>
  );
}
